import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { api, authAPI, subscriptionAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { PublicPlanDefinition, SubscriptionTier } from '../types/platformOrganization';
import { LoadingSpinner } from '../components/ui';

type SignupForm = {
  funerariaName: string;
  tenantSlug: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  planTier: SubscriptionTier;
  code: string;
};

const TIERS: SubscriptionTier[] = ['BASIC', 'PROFESSIONAL', 'PREMIUM'];

function parsePlanTier(value: string | null): SubscriptionTier {
  const upper = (value || '').toUpperCase();
  return TIERS.includes(upper as SubscriptionTier) ? (upper as SubscriptionTier) : 'BASIC';
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialTier = useMemo(() => parsePlanTier(searchParams.get('plan')), [searchParams]);

  const { data: plansRes, isLoading: plansLoading } = useQuery(['subscription-plans-public'], () =>
    subscriptionAPI.listPlans()
  );

  const plans = (plansRes?.data?.data as PublicPlanDefinition[] | undefined) || [];

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<SignupForm>({
    defaultValues: {
      planTier: initialTier,
      code: '',
    },
  });

  const selectedTier = watch('planTier');
  const selectedPlan = plans.find((p) => p.tier === selectedTier);
  const emailValue = watch('email');

  const sendCode = async () => {
    const data = getValues();
    if (data.password !== data.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setIsSubmitting(true);
    try {
      await authAPI.signupSendCode({
        name: data.name,
        email: data.email.trim().toLowerCase(),
        funerariaName: data.funerariaName,
        tenantSlug: data.tenantSlug || undefined,
        planTier: data.planTier,
      });
      setStep('verify');
      toast.success('Código enviado. Revise su correo (vence en 10 minutos).');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ||
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'No se pudo enviar el código';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: SignupForm) => {
    if (step === 'form') {
      await sendCode();
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await authAPI.signupConfirm({
        name: data.name,
        email: data.email.trim().toLowerCase(),
        password: data.password,
        code: data.code.trim(),
        funerariaName: data.funerariaName,
        tenantSlug: data.tenantSlug || undefined,
        planTier: data.planTier,
      });

      const loginResult = await login(
        { email: data.email, password: data.password },
        { rememberSession: true }
      );
      if (loginResult.success) {
        toast.success('¡Cuenta creada! Tiene 30 días de prueba gratis.');
        navigate('/dashboard', { replace: true });
      } else {
        toast.success('Cuenta creada. Inicie sesión para comenzar su prueba gratis.');
        navigate('/login', { replace: true });
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ||
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'No se pudo completar el registro';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formFieldsDisabled = step === 'verify';

  return (
    <div className="min-h-screen bg-[#05080f] text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-2xl backdrop-blur-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight">Registrar funeraria</h1>
          <p className="mt-2 text-sm text-slate-400">
            {step === 'form'
              ? 'Complete sus datos; le enviaremos un código al correo.'
              : `Ingrese el código enviado a ${emailValue || 'su correo'}.`}
          </p>
        </div>

        {plansLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <fieldset disabled={formFieldsDisabled} className="space-y-4 disabled:opacity-60">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Plan</label>
                <select
                  {...register('planTier', { required: true })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white"
                >
                  {TIERS.map((tier) => {
                    const plan = plans.find((p) => p.tier === tier);
                    return (
                      <option key={tier} value={tier} className="bg-slate-900">
                        {plan?.label || tier} — ${plan?.priceUsd ?? '?'}/mes
                      </option>
                    );
                  })}
                </select>
                {step === 'form' && (
                  <p className="mt-1 text-xs text-slate-500">
                    Incluye <strong className="text-slate-300">30 días de prueba gratis</strong> del plan{' '}
                    {selectedPlan?.label || selectedTier}. El pago se solicita al finalizar la prueba.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Nombre de la funeraria</label>
                <input
                  {...register('funerariaName', {
                    required: 'Requerido',
                    minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                  })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm"
                  placeholder="Ej. Funeraria Paz y Esperanza"
                />
                {errors.funerariaName && (
                  <p className="mt-1 text-xs text-red-400">{errors.funerariaName.message}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Identificador (opcional)</label>
                <input
                  {...register('tenantSlug', {
                    pattern: {
                      value: /^[a-z0-9-]*$/,
                      message: 'Solo minúsculas, números y guiones',
                    },
                  })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm"
                  placeholder="paz-esperanza"
                />
                {errors.tenantSlug && (
                  <p className="mt-1 text-xs text-red-400">{errors.tenantSlug.message}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Su nombre</label>
                <input
                  {...register('name', {
                    required: 'Requerido',
                    minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                  })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm"
                />
                {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name.message}</p>}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Correo</label>
                <input
                  type="email"
                  {...register('email', { required: 'Requerido' })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm"
                />
                {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Contraseña</label>
                <input
                  type="password"
                  {...register('password', {
                    required: 'Requerida',
                    minLength: { value: 6, message: 'Mínimo 6 caracteres' },
                  })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm"
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">Confirmar contraseña</label>
                <input
                  type="password"
                  {...register('confirmPassword', { required: 'Requerida' })}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm"
                />
              </div>
            </fieldset>

            {step === 'verify' && (
              <>
                <input
                  type="email"
                  name="username"
                  autoComplete="username"
                  value={emailValue || ''}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="pointer-events-none absolute h-0 w-0 opacity-0"
                />
                <div>
                <label htmlFor="register-verification-code" className="mb-1 block text-xs font-medium text-slate-400">
                  Código de verificación (6 dígitos)
                </label>
                <input
                  id="register-verification-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  data-1p-ignore
                  data-lpignore="true"
                  className="w-full rounded-lg border border-indigo-500/40 bg-white/5 px-3 py-3 text-center text-lg tracking-[0.4em] font-semibold"
                  placeholder="000000"
                  {...register('code', {
                    required: 'Ingrese el código del correo',
                    pattern: { value: /^\d{6}$/, message: 'Debe ser un código de 6 dígitos' },
                    setValueAs: (value) => String(value).replace(/\D/g, '').slice(0, 6)
                  })}
                  autoComplete="one-time-code"
                />
                {errors.code && <p className="mt-1 text-xs text-red-400">{errors.code.message}</p>}
                <button
                  type="button"
                  onClick={() => {
                    setStep('form');
                  }}
                  className="mt-2 text-xs text-slate-400 hover:text-slate-300"
                >
                  ← Corregir datos
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={sendCode}
                  className="mt-1 ml-4 text-xs text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                >
                  Reenviar código
                </button>
              </div>
              </>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 w-full rounded-lg bg-gradient-to-r from-indigo-500 to-violet-600 py-3 text-sm font-semibold text-white shadow-lg disabled:opacity-60"
            >
              {isSubmitting
                ? step === 'form'
                  ? 'Enviando código…'
                  : 'Verificando…'
                : step === 'form'
                  ? 'Enviar código al correo'
                  : 'Verificar y crear cuenta'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-slate-400">
          ¿Ya tiene cuenta?{' '}
          <Link to="/login" className="font-medium text-indigo-400 hover:text-indigo-300">
            Iniciar sesión
          </Link>
        </p>
        <p className="mt-2 text-center">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-400">
            Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
